/**
 * Guided registration — mirrors the mobile app's flow 1:1.
 *
 *   Step 1 · Site Code      → GET /api/sites/lookup?code=…
 *   Step 2 · Crew + Trade   → validated against the site config from step 1
 *   Step 3 · Role + Profile → collects role, name, email, PIN
 *   Step 4 · Email verify   → Supervisor+ ONLY. Chunky 6-box OTP screen.
 *   Step 5 · POST /api/auth/register
 *
 * Josh (Feb 27): "Keep UI chunky, simple, dark charcoal + orange. No
 * clutter." Every touch target is ≥ 48px; nothing extra on screen.
 *
 * Enterprise hardening (Feb 27 refinement): Supervisor / Superintendent /
 * Owner registrations MUST verify their email address via a 6-digit OTP
 * before the account is finalised — this way the future 3-day 2FA grace
 * window is guaranteed to have a real, delivered mailbox behind it.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { ArrowLeft, ArrowRight, Loader2, Check, ShieldAlert, HardHat, Wrench, ShieldCheck, Crown } from "lucide-react";
import OtpChallenge from "@/pages/OtpChallenge";

// Kept in sync with backend security_routes.py ELEVATED_ROLES + frontend
// permissions.js SUPERVISOR_TIER.
const ELEVATED_ROLES = new Set(["supervisor", "superintendent", "owner"]);

const ROLE_OPTIONS = [
  { code: "worker",         label: "Worker / Technician", icon: Wrench,       desc: "Inspections, handovers, fault-finder" },
  { code: "supervisor",     label: "Supervisor",          icon: HardHat,      desc: "Approve inspections, crew dashboard" },
  { code: "superintendent", label: "Superintendent",      icon: ShieldCheck,  desc: "Manage assets, people, templates" },
  { code: "owner",          label: "Owner / Control",     icon: Crown,        desc: "Full site + platform admin" },
];

export default function RegisterUser() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [siteCode, setSiteCode] = useState("");
  const [site, setSite] = useState(null); // { site_name, crews[], trades[] }
  const [crew, setCrew] = useState("");
  const [trade, setTrade] = useState("");
  const [role, setRole] = useState("worker");
  const [form, setForm] = useState({ name: "", email: "", pin: "", pin2: "" });
  const [verifyToken, setVerifyToken] = useState(null);

  const roleNeedsVerify = ELEVATED_ROLES.has(role);

  const lookup = async () => {
    if (!siteCode.trim()) { setErr("Enter your site code."); return; }
    setBusy(true); setErr("");
    try {
      const { data } = await api.get(`/sites/lookup?code=${encodeURIComponent(siteCode.trim().toUpperCase())}`);
      if (!data?.site_name) throw new Error("Site not found — check the code with your supervisor.");
      setSite(data);
      setStep(2);
    } catch (ex) {
      setErr(apiError(ex));
    } finally { setBusy(false); }
  };

  const next2 = () => {
    if (!crew) { setErr("Select your crew."); return; }
    if (!trade) { setErr("Select your trade."); return; }
    setErr(""); setStep(3);
  };

  // Called from Step 3. Elevated roles route through the email verify step
  // first; worker/technician submits directly.
  const submitStep3 = async () => {
    if (!form.name.trim()) { setErr("Full name is required."); return; }
    if (!/^\S+@\S+\.\S+$/.test(form.email)) { setErr("Enter a valid email."); return; }
    if (!/^\d{4}$/.test(form.pin)) { setErr("PIN must be exactly 4 digits."); return; }
    if (form.pin !== form.pin2) { setErr("PINs don't match."); return; }

    if (roleNeedsVerify) {
      // Kick off email OTP send, then transition to Step 4.
      setBusy(true); setErr("");
      try {
        await api.post("/auth/register/send-verify-otp", { email: form.email.trim() });
        setStep(4);
      } catch (ex) {
        setErr(apiError(ex));
      } finally { setBusy(false); }
      return;
    }
    // Worker: register immediately.
    await finalize();
  };

  const finalize = async (tokenOverride) => {
    setBusy(true); setErr("");
    try {
      await api.post("/auth/register", {
        name: form.name.trim(),
        email: form.email.trim(),
        pin: form.pin,
        site_code: (site?.code || siteCode).toUpperCase(),
        crew,
        trade,
        role,
        email_verified_token: tokenOverride || verifyToken || undefined,
      });
      navigate("/", { replace: true });
    } catch (ex) {
      setErr(apiError(ex));
      // If the token got rejected, kick the user back to the verify step.
      const msg = apiError(ex).toLowerCase();
      if (msg.includes("verification") || msg.includes("token")) {
        setVerifyToken(null);
        setStep(4);
      }
    } finally { setBusy(false); }
  };

  const onEmailVerified = async (data) => {
    const token = data?.email_verified_token;
    if (!token) {
      setErr("Verification succeeded but no token returned. Try again.");
      return;
    }
    setVerifyToken(token);
    // Immediately push through the final registration call.
    await finalize(token);
  };

  // Special render for Step 4 — full OtpChallenge screen (chunky 6-box).
  if (step === 4) {
    const maskEmail = (e) => {
      if (!e || !e.includes("@")) return "your email";
      const [l, d] = e.split("@");
      return `${l.slice(0, 1)}***@${d}`;
    };
    return (
      <OtpChallenge
        email={form.email.trim()}
        emailHint={maskEmail(form.email.trim())}
        verifyPath="/auth/register/verify-otp"
        resendPath="/auth/register/send-verify-otp"
        title="Verify your email"
        subtitle="Enter the 6-digit code we sent to"
        onVerified={onEmailVerified}
        onCancel={() => { setErr(""); setStep(3); }}
      />
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" data-testid="register-page"
         style={{ background: "linear-gradient(180deg, #0b0b0d 0%, #14141a 100%)" }}>
      <div className="w-full max-w-md" style={{ background: "rgba(20,20,26,0.9)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 16, padding: 24 }}>
        <div className="flex items-center gap-3 mb-6">
          {step > 1 && (
            <button className="btn btn-ghost btn-sm !px-2" onClick={() => { setErr(""); setStep(step - 1); }} data-testid="reg-back">
              <ArrowLeft className="w-4 h-4" />
            </button>
          )}
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest text-muted">Sign up</div>
            <div className="font-display font-black text-2xl leading-tight">
              {step === 1 && "Enter site code"}
              {step === 2 && (site?.site_name || siteCode)}
              {step === 3 && "Your profile"}
            </div>
            {step === 2 && site?.code && (
              <div className="text-[11px] font-mono-tight text-muted mt-0.5">{site.code}</div>
            )}
          </div>
          <StepDots current={step} total={3} />
        </div>

        {err && (
          <div className="pill pill-red !text-sm !py-2 !px-3 mb-4 flex items-center gap-2" data-testid="reg-error">
            <ShieldAlert className="w-4 h-4" /> {err}
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-muted">Site Code</label>
              <input
                className="input !text-lg !py-3 !mt-1 uppercase tracking-widest font-mono-tight"
                value={siteCode}
                onChange={(e) => setSiteCode(e.target.value.toUpperCase())}
                placeholder="e.g. WHC001"
                data-testid="reg-site-code"
                onKeyDown={(e) => e.key === "Enter" && lookup()}
                autoFocus
              />
            </div>
            <button
              className="btn btn-primary w-full !text-base !py-3"
              onClick={lookup}
              disabled={busy}
              data-testid="reg-step1-next"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              Next
            </button>
            <div className="text-[11px] text-muted text-center">
              Don&apos;t have a code? Contact your supervisor.
            </div>
          </div>
        )}

        {step === 2 && site && (
          <div className="space-y-5">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted mb-2">Your crew</div>
              <div className="flex flex-wrap gap-2">
                {(site.crews || []).map((c) => (
                  <Chip key={c} active={crew === c} onClick={() => setCrew(c)} testid={`reg-crew-${c.toLowerCase().replace(/\s+/g, "-")}`}>{c}</Chip>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted mb-2">Your trade</div>
              <div className="flex flex-wrap gap-2">
                {(site.trades || []).map((t) => (
                  <Chip key={t} active={trade === t} onClick={() => setTrade(t)} testid={`reg-trade-${t.toLowerCase().replace(/\s+/g, "-")}`}>{t}</Chip>
                ))}
              </div>
            </div>
            <button
              className="btn btn-primary w-full !text-base !py-3"
              onClick={next2}
              disabled={!crew || !trade}
              data-testid="reg-step2-next"
            >
              <ArrowRight className="w-4 h-4" /> Next
            </button>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted mb-2">Access level</div>
              <div className="grid grid-cols-1 gap-2" data-testid="reg-role-picker">
                {ROLE_OPTIONS.map((r) => {
                  const Icon = r.icon;
                  const active = role === r.code;
                  return (
                    <button
                      key={r.code}
                      onClick={() => setRole(r.code)}
                      data-testid={`reg-role-${r.code}`}
                      className="text-left px-3 py-3 rounded-lg transition-all flex items-center gap-3"
                      style={active
                        ? { background: "rgba(245,130,32,0.18)", border: "1px solid var(--primary)", color: "#fff", minHeight: 56 }
                        : { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.85)", minHeight: 56 }}
                    >
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                           style={{ background: active ? "rgba(245,130,32,0.25)" : "rgba(255,255,255,0.05)",
                                    color: active ? "var(--primary)" : "rgba(255,255,255,0.6)" }}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-black">{r.label}</div>
                        <div className="text-[10px] text-muted mt-0.5">{r.desc}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
              {roleNeedsVerify && (
                <div className="text-[10px] mt-2 text-muted flex items-start gap-1.5">
                  <ShieldCheck className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: "var(--primary)" }} />
                  Elevated roles must verify their email with a 6-digit code before the account is created.
                </div>
              )}
            </div>
            <FormField label="Full name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="reg-name" />
            <FormField label="Email" hint="For security codes and reports" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="reg-email" type="email" />
            <FormField label="4-digit PIN" value={form.pin} onChange={(v) => setForm({ ...form, pin: v.replace(/\D/g, "").slice(0, 4) })} testid="reg-pin" type="password" inputMode="numeric" />
            <FormField label="Confirm PIN" value={form.pin2} onChange={(v) => setForm({ ...form, pin2: v.replace(/\D/g, "").slice(0, 4) })} testid="reg-pin2" type="password" inputMode="numeric" />
            <button
              className="btn btn-primary w-full !text-base !py-3 mt-2"
              onClick={submitStep3}
              disabled={busy}
              data-testid="reg-submit"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (roleNeedsVerify ? <ShieldCheck className="w-4 h-4" /> : <Check className="w-4 h-4" />)}
              {roleNeedsVerify ? "Verify email · Next" : "Create account"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function StepDots({ current, total }) {
  return (
    <div className="flex items-center gap-1.5">
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} className="w-2 h-2 rounded-full transition-colors"
             style={{ background: i + 1 <= current ? "var(--primary)" : "rgba(255,255,255,0.18)" }} />
      ))}
    </div>
  );
}

function Chip({ active, onClick, testid, children }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="px-4 py-3 rounded-lg text-sm font-black transition-all"
      style={active
        ? { background: "rgba(245,130,32,0.22)", border: "1px solid var(--primary)", color: "#fff", minHeight: 48 }
        : { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)", color: "rgba(255,255,255,0.85)", minHeight: 48 }}
    >
      {children}
    </button>
  );
}

function FormField({ label, hint, value, onChange, testid, type = "text", inputMode }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-muted">{label}</label>
      <input
        className="input !text-base !py-3 !mt-1"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        type={type}
        inputMode={inputMode}
      />
      {hint && <div className="text-[10px] text-muted mt-1">{hint}</div>}
    </div>
  );
}
